import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Home from "@/pages/Home";
import Loading from "@/pages/Loading";
import Results from "@/pages/Results";
import CardDetail from "@/pages/CardDetail";
import History from "@/pages/History";
import Dashboard from "@/pages/Dashboard";
import Leaderboard from "@/pages/Leaderboard";
import PremiumSuccess from "@/pages/PremiumSuccess";
import RenderCard from "@/pages/RenderCard";
import ExportCardPreview from "@/pages/ExportCardPreview";
import PublicCard from "@/pages/PublicCard";
import Remix from "@/pages/Remix";
import RemixFeed from "@/pages/RemixFeed";
import Exhausted from "@/pages/Exhausted";
import Thread from "@/pages/Thread";
import Explore from "@/pages/Explore";
import Creators from "@/pages/Creators";
import CreatorProfile from "@/pages/CreatorProfile";
import MyVibe from "@/pages/MyVibe";
import Appearance from "@/pages/Appearance";
import RivalryHub from "@/pages/RivalryHub";
import AdminSports from "@/pages/AdminSports";
import CommandPalette from "@/components/CommandPalette";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Challenge from "@/pages/Challenge";
import NotFound from "@/pages/NotFound";
import InstallBanner from "@/components/InstallBanner";
import BrandBadge from "@/components/BrandBadge";
import PremiumModal from "@/components/PremiumModal";
import SupportChat from "@/components/SupportChat";
import AuthCallback from "@/lib/AuthCallback";
import { ThemeProvider } from "@/lib/ThemeContext";
import { AuthProvider } from "@/lib/AuthContext";

function AppRouter() {
    const location = useLocation();
    // iter-93 — global premium modal mount. Any page can trigger it via
    // window.dispatchEvent(new CustomEvent("open-premium-modal", { detail: { source } }))
    const [premiumOpen, setPremiumOpen] = React.useState(false);
    const [premiumSource, setPremiumSource] = React.useState("generic");
    React.useEffect(() => {
        const onOpen = (e) => {
            setPremiumSource(e?.detail?.source || "generic");
            setPremiumOpen(true);
        };
        window.addEventListener("open-premium-modal", onOpen);
        // iter-102 — auth modal event listener. For now we go straight to
        // Google OAuth redirect (zero-friction) since that's the only
        // working provider. The pending checkout intent is stashed by the
        // caller; AuthCallback resumes it after sign-in.
        const onAuth = (e) => {
            const resume = e?.detail?.resume_checkout;
            if (resume) {
                try {
                    window.sessionStorage.setItem("moodcard.pending_checkout", JSON.stringify({
                        product_id: resume, ts: Date.now(),
                    }));
                } catch { /* ignore */ }
            }
            const redirect = window.location.origin + "/";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
        };
        window.addEventListener("open-auth-modal", onAuth);
        return () => {
            window.removeEventListener("open-premium-modal", onOpen);
            window.removeEventListener("open-auth-modal", onAuth);
        };
    }, []);
    // iter-104 — open the Premium modal when arriving with ?premium=1
    // (used by CommandPalette and the "upgrade for more" CTA).
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("premium") === "1") {
            setPremiumSource(params.get("source") || "url");
            setPremiumOpen(true);
            // Strip the param so back/refresh doesn't re-open.
            const cleanedSearch = new URLSearchParams(location.search);
            cleanedSearch.delete("premium");
            cleanedSearch.delete("source");
            const next = location.pathname + (cleanedSearch.toString() ? `?${cleanedSearch.toString()}` : "");
            window.history.replaceState({}, "", next);
        }
    }, [location.pathname, location.search]);
    // Sync session_id detection BEFORE Routes (race-free).
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/loading" element={<Loading />} />
                <Route path="/results/:id" element={<Results />} />
                <Route path="/card/:id/:style" element={<CardDetail />} />
                <Route path="/c/:slug" element={<PublicCard />} />
                <Route path="/open/:slug" element={<PublicCard />} />
                <Route path="/remix/:slug" element={<Remix />} />
                <Route path="/c/:slug/remixes" element={<RemixFeed />} />
                <Route path="/exhausted" element={<Exhausted />} />
                <Route path="/thread/:id" element={<Thread />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/discover" element={<Explore />} />
                <Route path="/creators" element={<Creators />} />
                <Route path="/creators/:creatorId" element={<CreatorProfile />} />
                <Route path="/my-vibe" element={<MyVibe />} />
                <Route path="/appearance" element={<Appearance />} />
                <Route path="/r/:slug" element={<RivalryHub />} />
                <Route path="/admin/sports" element={<AdminSports />} />
                <Route path="/history" element={<History />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/premium/success" element={<PremiumSuccess />} />
                <Route path="/premium/cancel" element={<Home />} />
                <Route path="/render/:id/:style" element={<RenderCard />} />
                <Route path="/render-export/:id/:style" element={<ExportCardPreview />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/challenge/:slug" element={<Challenge />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <InstallBanner />
            <BrandBadge />
            <CommandPalette />
            <PremiumModal open={premiumOpen} onOpenChange={setPremiumOpen} source={premiumSource} />
            <SupportChat />
        </>
    );
}

function App() {
    return (
        <div className="App min-h-screen bg-app text-app">
            <HelmetProvider>
                <ThemeProvider>
                    <AuthProvider>
                        <BrowserRouter>
                            <AppRouter />
                        </BrowserRouter>
                    </AuthProvider>
                </ThemeProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
